/**
 * Easter banners — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterBannersPack: ToilePack = {
    slug: 'easter-banners',
    category: 'season',
    name: 'Easter banners',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'pennants', label: 'Pennant banner', subject: 'A printable pennant banner of alternating flags on a string, each flag holding one letter of "HAPPY EASTER", dashed cut lines'},
        {key: 'eggs', label: 'Egg banner', subject: 'A long horizontal banner with painted eggs along its length and a central ribbon reading "HOP", dashed cut lines'},
        {key: 'carrots', label: 'Carrot swag', subject: 'A string of paper carrots to cut and hang, each with leafy tops, dashed cut lines, no lettering'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel', style: 'pastel easter printable, egg and floral borders, soft rounded lettering, print-ready', palette: 'Soft pink, mint, lilac and butter yellow', framing: 'Laid out on a vertical page with an even white margin'},
        {key: 'vintage', label: 'Vintage', style: 'vintage easter printable, engraved floral borders, elegant serif lettering, print-ready', palette: 'Rose, sage, cream and a little gold', framing: 'Laid out on a cream page with an ornate border and a margin'},
        {key: 'kids', label: 'Kids', style: "kids' easter printable, bright cartoon bunnies and chicks, rounded lettering, print-ready", palette: 'Bright yellow, sky blue, grass green and pink', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['for a church brunch', 'as a mini garland', "spelling a child's name"],
    directionHint: "try: spelling a child's name",
    seo: {
        title: 'Easter banner: pennants, eggs and carrots',
        metaDescription: "Nine Easter banners — lettered pennants, an egg garland, a paper carrot swag — pastel, vintage or kids' style, cut lines drawn.",
        h1: 'Easter banner: pennants, eggs and carrots in three styles',
        tagline: 'Nine sheets, three banner styles, three finishes each — from the lettered pennant to the carrot swag.',
        intro: "Three Easter banners for the table or wall: lettered pennants spelling a greeting, an egg garland banner, a swag of paper carrots. Every sheet marks its cut lines, drawn in a soft pastel look, a vintage two-tone finish, or a bright kids' style. String on twine and hang over the table or mantel. Type your own greeting and the set redraws.",
        useCases: [
            'an Easter brunch table backdrop',
            'a mantel or doorway decoration',
            'a classroom spring-themed bulletin board',
            'an egg hunt party decoration',
        ],
        faq: [
            {q: 'What does the banner spell out?', a: "The pennant set is lettered 'HAPPY EASTER' by default; type your own greeting and it redraws with your wording instead."},
            {q: 'What do I need to assemble it?', a: 'Cut out the pieces, punch the marked holes, and string them on twine or ribbon — no glue required.'},
            {q: 'What paper should I print on?', a: 'Card stock holds its shape best for hanging; standard printer paper works for a quick, lighter version.'},
            {q: "Is there a softer version for a baby's first Easter?", a: "The pastel set leans soft and gentle for exactly that, while the vintage set carries more contrast and the kids' set stays bright and playful."},
        ],
    },
}
